import type {ReactNode} from 'react'
import type {CompletedReview, ReviewState} from '../../../../../page-data/payloads/review-requests'
import {ReviewerIdentity} from '../../../reviewers/ReviewerIdentity'
import styles from './ReviewHeaderContent.module.css'

interface ReviewHeaderContent {
  title: string | ReactNode
  description?: ReactNode
}

const LABELS: Partial<Record<ReviewState, {singular: string; plural: string}>> = {
  approved: {singular: 'approval received', plural: 'approvals received'},
  changes_requested: {singular: 'change requested', plural: 'changes requested'},
  commented: {singular: 'comment review', plural: 'comment reviews'},
}

/**
 * Builds the title and description for a review group header, following these rules:
 * - 1 reviewer: title = "1 {singular}", middot, <ReviewerIdentity> — no description
 * - 2 reviewers: title = "2 {plural}", description = <Identity> and <Identity>
 * - 3 reviewers: title = "3 {plural}", description = <Identity>, <Identity>, and <Identity>
 * - 4+ reviewers: title = "N {plural}", description = <Identity>, <Identity>, and N-2 others
 */
export function buildReviewHeaderContent(reviews: CompletedReview[], state: ReviewState): ReviewHeaderContent {
  const {singular: singularLabel = state, plural: pluralLabel = state} = LABELS[state] ?? {}
  const count = reviews.length
  const [first, second, third] = reviews

  if (count === 1 && first) {
    return {
      title: (
        <>
          <span className={styles.title}>1 {singularLabel}</span>
          <span aria-hidden="true" className={styles.middot}>
            ·
          </span>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={first.reviewer} withAvatar={false} withLeadingAt />
          </span>
        </>
      ),
    }
  }

  const titleText = `${count} ${pluralLabel}`

  if (count === 2 && first && second) {
    return {
      title: titleText,
      description: (
        <span className={styles.description}>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={first.reviewer} withAvatar={false} withLeadingAt />
          </span>
          <span>and</span>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={second.reviewer} withAvatar={false} withLeadingAt />
          </span>
        </span>
      ),
    }
  }

  if (count === 3 && first && second && third) {
    return {
      title: titleText,
      description: (
        <span className={styles.description}>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={first.reviewer} withAvatar={false} withLeadingAt />
            <span>,</span>
          </span>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={second.reviewer} withAvatar={false} withLeadingAt />
            <span>, and</span>
          </span>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={third.reviewer} withAvatar={false} withLeadingAt />
          </span>
        </span>
      ),
    }
  }

  // 4+ reviewers: show first two + "and N-2 others"
  return {
    title: titleText,
    description:
      first && second ? (
        <span className={styles.description}>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={first.reviewer} withAvatar={false} withLeadingAt />
            <span>,</span>
          </span>
          <span className={styles.reviewerIdentityWrapper}>
            <ReviewerIdentity reviewer={second.reviewer} withAvatar={false} withLeadingAt />
            {`, and ${count - 2} others`}
          </span>
        </span>
      ) : undefined,
  }
}
